import '@angular/localize/init';
import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DcAlertModule } from '@datachain/ui-sdk/components/alert';
import { DcBadgeModule } from '@datachain/ui-sdk/components/app-badge';
import { DcButtonModule } from '@datachain/ui-sdk/components/app-button';
import { DcCardOutlineModule } from '@datachain/ui-sdk/components/app-card-outline';
import { DcChipModule } from '@datachain/ui-sdk/components/app-chip';
import { DcColumnTypeModule } from '@datachain/ui-sdk/components/app-column-type';
import { DcColumnsConfiguratorModule } from '@datachain/ui-sdk/components/app-columns-configurator';
import { DcMultiSelectMenuModule } from '@datachain/ui-sdk/components/app-multi-select-menu';
import { DcOverlayBadgeModule } from '@datachain/ui-sdk/components/app-overlay-badge';
import { DcPageModule } from '@datachain/ui-sdk/components/app-page';
import { DcPanelModule } from '@datachain/ui-sdk/components/app-panel';
import { DcPopoverModule } from '@datachain/ui-sdk/components/app-popover';
import { DcToggleButtonsModule } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { DcAvatarModule } from '@datachain/ui-sdk/components/avatar';
import { DcBreadcrumbModule } from '@datachain/ui-sdk/components/breadcrumb';
import { DcdsQueryBuilderModule } from '@datachain/ui-sdk/components/query-builder';
import { DcdsQueryViewModule } from '@datachain/ui-sdk/components/query-view';
import { DcNavigateDirectiveModule } from '@datachain/ui-sdk/directives';
import {
	DcArrayPipeModule,
	DcDayjsDatePipeModule,
	DcTruncatePipeModule,
} from '@datachain/ui-sdk/pipes';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import {
	DxDataGridModule,
	DxTabsModule,
	DxTextBoxModule,
	DxValidatorModule,
} from 'devextreme-angular';

import { MaterialModule } from '../ui/material.module';
import { UiModule } from '../ui/ui.module';
// eslint-disable-next-line max-len
import { ColumnModelActionConfirmPopupComponent } from './components/column-models/column-model-action-confirm-popup/column-model-action-confirm-popup.component';
import { ColumnModelConfigModalComponent } from './components/column-models/column-model-config-modal/column-model-config-modal.component';
import { ColumnModelsComponent } from './components/column-models/column-models.component';
import { ColumnsConfigComponent } from './components/columns-config/columns-config.component';
import { FilterModelActionConfirmPopupComponent } from './components/filter-models/filter-model-action-confirm-popup/filter-model-action-confirm-popup.component';
import { FilterModelConfigModalComponent } from './components/filter-models/filter-model-config-modal/filter-model-config-modal.component';
import { FilterModelsComponent } from './components/filter-models/filter-models.component';
import { InternalMetadataConfigComponent } from './components/internal-metadata-config/internal-metadata-config.component';
import { ProfileConfigComponent } from './components/profile-config/profile-config.component';
import { ProfileActionConfirmPopupComponent } from './components/profiles/profile-action-confirm-popup/profile-action-confirm-popup.component';
import { ProfileConfigModalComponent } from './components/profiles/profile-config-modal/profile-config-modal.component';
import { ProfilesComponent } from './components/profiles/profiles.component';
import { PublicMetadataConfigComponent } from './components/public-metadata-config/public-metadata-config.component';
import { SubscriptionActionConfirmPopupComponent } from './components/subscription-action-confirm-popup/subscription-action-confirm-popup.component';
import { SubscriptionConfigurationStepperComponent } from './components/subscription-configuration-stepper/subscription-configuration-stepper.component';
import { SubscriptionListStandaloneComponent } from './components/subscription-list-standalone/subscription-list-standalone.component';
import { SubscriptionListWrapperComponent } from './components/subscription-list-wrapper/subscription-list-wrapper.component';
import { SubscriptionListComponent } from './components/subscription-list/subscription-list.component';
import { ModelsSelectionModalComponent } from './components/subscription-simulator/models-selection-modal/models-selection-modal.component';
import { ProfileSelectionModalComponent } from './components/subscription-simulator/profile-selection-modal/profile-selection-modal.component';
import { SubscriptionSimulatorComponent } from './components/subscription-simulator/subscription-simulator.component';
import { ActionConfirmPopupComponent } from './components/subscription-view/action-confirm-popup/action-confirm-popup.component';
import { ColumnsConfigModalComponent } from './components/subscription-view/columns-config-modal/columns-config-modal.component';
import { ConsumerConfigModalComponent } from './components/subscription-view/consumer-config-modal/consumer-config-modal.component';
import { HistoryListModalComponent } from './components/subscription-view/history-list-modal/history-list-modal.component';
import { InternalMetadataPopupComponent } from './components/subscription-view/internal-metadata-popup/internal-metadata-popup.component';
import { PermissionsConfigModalComponent } from './components/subscription-view/permissions-config-modal/permissions-config-modal.component';
import { PublicMetadataPopupComponent } from './components/subscription-view/public-metadata-popup/public-metadata-popup.component';
import { SubscriptionViewComponent } from './components/subscription-view/subscription-view.component';
import { VersionSwitcherModalComponent } from './components/version-switcher-modal/version-switcher-modal.component';
import { NgProjectSubscription } from './ng-project-subscription';
import { ProjectSubscriptionAdapter } from './project-subscription.adapter';
import {
	SubscriptionCreatorEffects,
	SubscriptionCreatorSelector,
	SubscriptionEffects,
	SubscriptionFeatureKey,
	subscriptionReducers,
	SubscriptionSelector,
	SubscriptionSimulatorEffects,
	SubscriptionSimulatorSelector,
} from './store';
import { SubscriptionDataMockService } from './subscription-data.mock.service';

@NgModule({
	declarations: [
		SubscriptionListComponent,
		SubscriptionListStandaloneComponent,
		SubscriptionListWrapperComponent,
		SubscriptionConfigurationStepperComponent,
		InternalMetadataConfigComponent,
		PublicMetadataConfigComponent,
		ColumnsConfigComponent,
		SubscriptionViewComponent,
		PermissionsConfigModalComponent,
		InternalMetadataPopupComponent,
		PublicMetadataPopupComponent,
		ConsumerConfigModalComponent,
		HistoryListModalComponent,
		ProfilesComponent,
		ColumnModelsComponent,
		ColumnModelConfigModalComponent,
		ColumnModelActionConfirmPopupComponent,
		FilterModelsComponent,
		FilterModelActionConfirmPopupComponent,
		ColumnsConfigModalComponent,
		ProfileConfigModalComponent,
		ProfileActionConfirmPopupComponent,
		FilterModelConfigModalComponent,
		ProfileConfigComponent,
		SubscriptionSimulatorComponent,
		ProfileSelectionModalComponent,
		ModelsSelectionModalComponent,
		SubscriptionActionConfirmPopupComponent,
		ActionConfirmPopupComponent,
		VersionSwitcherModalComponent,
	],
	imports: [
		CommonModule,
		MaterialModule,
		FormsModule,
		ReactiveFormsModule,
		UiModule,
		DcPanelModule,
		StoreModule.forFeature(SubscriptionFeatureKey, subscriptionReducers),
		EffectsModule.forFeature([
			SubscriptionEffects,
			SubscriptionSimulatorEffects,
			SubscriptionCreatorEffects,
		]),
		DcArrayPipeModule,
		DxDataGridModule,
		DcNavigateDirectiveModule,
		DcAvatarModule,
		DcDayjsDatePipeModule,
		DcOverlayBadgeModule,
		DcPopoverModule,
		DcColumnTypeModule,
		DcCardOutlineModule,
		DcPageModule,
		DxTextBoxModule,
		DxTabsModule,
		DcTruncatePipeModule,
		DcBreadcrumbModule,
		DcBadgeModule,
		DcButtonModule,
		DcChipModule,
		DcMultiSelectMenuModule,
		DcColumnsConfiguratorModule,
		DcAlertModule,
		DcdsQueryBuilderModule,
		DxValidatorModule,
		DcdsQueryViewModule,
		DcToggleButtonsModule,
	],
	providers: [
		NgProjectSubscription,
		ProjectSubscriptionAdapter,
		SubscriptionDataMockService,
		SubscriptionSelector,
		SubscriptionSimulatorSelector,
		SubscriptionCreatorSelector,
	],
})
export class ProjectSubscriptionModule {
	public constructor(private readonly injector: Injector) {
		const subscriptionListStandaloneCmpElement = createCustomElement(
			SubscriptionListStandaloneComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-subscription-list-standalone',
			subscriptionListStandaloneCmpElement
		);

		const subscriptionListWrapperCmpElement = createCustomElement(
			SubscriptionListWrapperComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-subscription-list-wrapper',
			subscriptionListWrapperCmpElement
		);

		const subscriptionViewCmpElement = createCustomElement(
			SubscriptionViewComponent,
			{
				injector,
			}
		);
		customElements.define('app-subscription-view', subscriptionViewCmpElement);

		const subscriptionSimulatorCmpElement = createCustomElement(
			SubscriptionSimulatorComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-subscription-simulator',
			subscriptionSimulatorCmpElement
		);
	}
}
